import { useToasted } from './useToasted';
import { ref, computed } from 'vue';
import { useCursorPagination } from './useCursorPagination';
import dayjs from '~/utils/dayjs';
import { getClientTz } from '~/utils/date';
import {
    actionsClient,
    groupsClient,
    messagesClient,
    scheduledMessagesClient,
    teamsClient,
    threadsClient,
} from '~/services';
import { catchRequestError } from '~/utils/request';
import { isAuthErrorEcho } from '~/utils/helpers';
import { createGlobalState } from '@vueuse/core';
import { reactive } from 'vue';
import { isNotActive } from '~/utils/team';
import {
    CONTACT_SUBSCRIPTION_STATUS_OPT_OUT,
    CONTACT_SUBSCRIPTION_TYPE_TRANSACTIONAL,
} from '~/constants/contactSubscription';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { VxSwal } from '@voxie/frontend-components';
import { FEATURES } from '~/provides/features';

export const useConversation = createGlobalState(() => {
    const toasted = useToasted();

    const teamId = Spark.state.currentTeam.id;

    const contact = ref();
    const activeThread = ref();
    const { list, fetchParams, loading: loadingMessages, isLast, fetchRecords } = useCursorPagination();
    const currentGroupId = ref();
    const agents = ref([]);
    const mounting = ref(false);
    const claimingThread = ref(false);
    const markingThreadAsRead = ref(false);
    const changingThreadState = ref(false);
    const fetchingAgents = ref(false);
    const isPhoneNumberSet = ref(false);
    const loadingPhoneNumbers = ref(true);
    const sendingMessage = ref(false);
    const message = reactive({
        body: '',
        media_url: null,
        media_content_type: null,
        send_at: null,
    });

    const isContactSubscribed = computed(() => {
        return (
            (contact.value?.subscriptions || []).find((s) => s.type === CONTACT_SUBSCRIPTION_TYPE_TRANSACTIONAL)
                ?.status !== CONTACT_SUBSCRIPTION_STATUS_OPT_OUT
        );
    });

    const resetMessageData = () => {
        message.body = '';
        message.media_url = null;
        message.media_content_type = null;
        message.send_at = null;
    };

    const isMessagingDisabled = computed(() => {
        return Boolean(
            isNotActive() ||
                loadingPhoneNumbers.value ||
                !isPhoneNumberSet.value ||
                !isContactSubscribed.value ||
                sendingMessage.value
        );
    });

    const getSendingPhoneNumber = async () => {
        try {
            const params = { contact_id: contact.value.id };

            if (!currentGroupId.value) {
                await messagesClient.getPhoneNumber(teamId, params);
            } else {
                await groupsClient.groupPhoneNumber(teamId, currentGroupId.value, params);
            }

            isPhoneNumberSet.value = true;
        } catch (e) {
            if (e?.response?.status !== HTTP_NOT_FOUND) {
                throw e;
            }
        } finally {
            loadingPhoneNumbers.value = false;
        }
    };

    const validateMessage = () => {
        if (!message.body || message.body.trim() === '') {
            VxSwal.fire({ title: 'Whoops!', text: 'Message body is required.', icon: 'error' });
            return false;
        }

        if (message.send_at) {
            if (!message.send_at) {
                VxSwal.fire({ title: 'Whoops!', text: 'Send At is required.', icon: 'error' });
                return false;
            }

            if (
                message.send_at !== null &&
                dayjs().tz(getClientTz()).utc().isSameOrAfter(dayjs.utc(message.send_at).tz(getClientTz()))
            ) {
                VxSwal.fire({ title: 'Whoops!', text: 'The send at field cannot be set in the past.', icon: 'error' });
                return false;
            }
        }

        if (!isPhoneNumberSet.value) {
            VxSwal.fire({ title: 'Whoops!', text: 'No phone numbers are available to send messages.', icon: 'error' });
            return false;
        }

        return true;
    };

    const deliverMessage = async (thorwError) => {
        if (isMessagingDisabled.value) {
            return;
        }

        if (!validateMessage()) {
            return;
        }

        await markAsRead();

        sendingMessage.value = true;

        const payload = {
            contact_id: contact.value.id,
            ...message,
        };

        try {
            if (!currentGroupId.value) {
                message.send_at
                    ? await scheduledMessagesClient.createScheduledMessage(teamId, contact.value.id, payload)
                    : await messagesClient.createV3(teamId, payload);
            } else {
                await messagesClient.groupMessageCreate(teamId, currentGroupId.value, payload);
            }

            actionsClient.send('message_sent');
            resetMessageData();
        } catch (e) {
            if (thorwError) {
                throw e;
            }

            VxSwal.fire({
                title: 'Whoops!',
                text: catchRequestError(e),
                icon: 'error',
            });
        } finally {
            sendingMessage.value = false;
        }
    };

    const actionInProgress = computed(() => {
        return Boolean(
            loadingMessages.value ||
                fetchingAgents.value ||
                claimingThread.value ||
                markingThreadAsRead.value ||
                changingThreadState.value ||
                loadingPhoneNumbers.value ||
                sendingMessage.value
        );
    });

    const defaultFilters = () => ({
        per_page: 15,
        cursor: null,
        sort_dir: 'desc',
    });

    fetchParams.value = defaultFilters();

    const isOwnThread = computed(() => {
        return activeThread.value && activeThread.value.assignee_id === Spark.state.user.id;
    });

    const agent = computed(() => {
        if (!activeThread.value?.assignee_id) {
            return;
        }

        return agents.value.find((a) => a.id === activeThread.value.assignee_id);
    });

    const agentName = computed(() => {
        if (!agent.value) {
            return;
        }

        if (agent.value.id === Spark.state.user.id) {
            return 'You';
        } else {
            return agent.value?.name || 'Unknown';
        }
    });

    const fetchAgents = async () => {
        if (fetchingAgents.value) {
            return;
        }

        fetchingAgents.value = true;

        const response = !currentGroupId.value
            ? await teamsClient.getTeamAgents(teamId)
            : await groupsClient.getGroupAgents(teamId, currentGroupId.value);

        agents.value = Array.isArray(response.data) ? response.data : [];

        fetchingAgents.value = false;
    };

    const shouldShowNewLine = (targetMessage) => {
        return Boolean(
            !FEATURES.simple_message_hub &&
                activeThread.value &&
                lastUnreadMessage.value &&
                targetMessage.id === lastUnreadMessage.value.id &&
                (targetMessage.body || targetMessage.media_url)
        );
    };

    const messageGroups = computed(() => {
        const messagesToDay = ([day, group]) => {
            const messages = group.sort((mA, mB) => mA.id - mB.id);
            return {
                lastUnreadInboundMessage: shouldShowNewLine(messages[0]),
                fullDate: day,
                prettyDate: dayjs(day).tz(getClientTz()).format('MMM Do, YYYY'),
                messages,
            };
        };

        const groups = list.value.reduce((groups, targetMessage) => {
            const groupKey = dayjs(targetMessage.created_at).tz(getClientTz()).startOf('day').toString();
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }

            groups[groupKey].push(targetMessage);

            return groups;
        }, {});

        return Object.entries(groups)
            .map(messagesToDay)
            .sort((gA, gB) => {
                return new Date(gB.fullDate).getTime() - new Date(gA.fullDate).getTime();
            });
    });

    const lastUnreadMessage = computed(() => {
        if (!activeThread.value) {
            return;
        }

        const messages = list.value;
        const flteredMessages = messages
            .sort((mA, mB) => mB.id - mA.id)
            .filter((m) => {
                // All messages except user's one can be unread.
                return (
                    m.direction === 'inbound' ||
                    (m.direction === 'outbound' &&
                        (m.origin_meta.type !== 'Message Hub' || m.origin_meta.id !== Spark.state.user.id))
                );
            });

        const unreadMessages = flteredMessages.slice(0, activeThread.value.unread);

        return unreadMessages[unreadMessages.length - 1];
    });

    const claimThread = async () => {
        if (!activeThread.value) {
            return;
        }

        if (FEATURES.simple_message_hub) {
            return;
        }

        if (isOwnThread.value) {
            return;
        }

        if (claimingThread.value) {
            return;
        }

        claimingThread.value = true;

        const response = !currentGroupId.value
            ? await threadsClient.threadsAssignToSelf(teamId, contact.value.id)
            : await threadsClient.groupThreadsAssignToSelf(teamId, currentGroupId.value, contact.value.id);

        activeThread.value.assignee_id = response.data?.assignee_id;

        claimingThread.value = false;
    };

    const markAsRead = async () => {
        if (!activeThread.value) {
            return;
        }

        if (markingThreadAsRead.value) {
            return;
        }

        if (FEATURES.simple_message_hub) {
            if (activeThread.value.status !== 'closed') {
                await changeActiveThreadState('closed');
            }

            return;
        }

        if (!isOwnThread.value) {
            return;
        }

        markingThreadAsRead.value = true;

        const result = !currentGroupId.value
            ? await threadsClient.threadsCheckpointSave(teamId, activeThread.value.contact_id)
            : await threadsClient.groupThreadsCheckpointSave(
                  teamId,
                  currentGroupId.value,
                  activeThread.value.contact_id
              );

        activeThread.value.unread = 0;
        activeThread.value.message_count = result.data?.message_count;

        markingThreadAsRead.value = false;
    };

    const loadMessages = async () => {
        await fetchRecords(
            !currentGroupId.value
                ? messagesClient.listMessages(teamId, contact.value.id, { ...fetchParams.value })
                : messagesClient.groupListMessages(teamId, currentGroupId.value, contact.value.id, {
                      ...fetchParams.value,
                  })
        );
    };

    const unmount = () => {
        unsubscribeEvents();

        contact.value = undefined;
        currentGroupId.value = undefined;
        activeThread.value = undefined;
        list.value = [];
        agents.value = [];
        fetchParams.value = defaultFilters();
        mounting.value = false;
        claimingThread.value = false;
        markingThreadAsRead.value = false;
        changingThreadState.value = false;
        fetchingAgents.value = false;
        isPhoneNumberSet.value = false;
        loadingPhoneNumbers.value = true;
        resetMessageData();
    };

    const mount = async (targetContact, groupId) => {
        if (!targetContact.id || !targetContact.phone) {
            return;
        }

        if (mounting.value) {
            return;
        }

        unmount();

        mounting.value = true;

        contact.value = targetContact;
        currentGroupId.value = groupId;

        subscribeEvents();

        await getSendingPhoneNumber();

        try {
            const threads =
                (!currentGroupId.value
                    ? await threadsClient.threadsGetAll(teamId, {
                          phone: contact.value.phone,
                      })
                    : await threadsClient.groupThreadsGetAll(teamId, currentGroupId.value, {
                          phone: contact.value.phone,
                      })
                )?.data?.data || [];

            if (!threads.length || threads[0]?.contact_id !== contact.value.id) {
                return;
            }

            activeThread.value = threads[0];
            if (FEATURES.simple_message_hub && activeThread.value && activeThread.value.status !== 'closed') {
                await changeActiveThreadState('closed');
            }

            if (!FEATURES.simple_message_hub) {
                await fetchAgents();
            }

            if (!activeThread.value) {
                return;
            }

            await loadMessages();
        } catch (e) {
            toasted.error(catchRequestError(e));
        } finally {
            mounting.value = false;
        }
    };

    const changeActiveThreadState = async (state) => {
        if (!activeThread.value) {
            return;
        }

        if (changingThreadState.value) {
            return;
        }

        changingThreadState.value = true;

        !currentGroupId.value
            ? await threadsClient.threadsState(teamId, contact.value.id, state)
            : await threadsClient.groupThreadsState(teamId, currentGroupId.value, contact.value.id, state);

        activeThread.value.status = state;
        changingThreadState.value = false;
    };

    const unsubscribeEvents = () => {
        Echo.leave(!currentGroupId.value ? `messages.${teamId}` : `messages.${teamId}.${currentGroupId.value}`);
    };

    const subscribeEvents = () => {
        const echoErrors = [];
        Echo.private(!currentGroupId.value ? `messages.${teamId}` : `messages.${teamId}.${currentGroupId.value}`)
            .listen('MessageCreated', (payload) => {
                if (!payload?.contact?.id) {
                    return;
                }

                if (payload.contact.id !== contact.value.id) {
                    return;
                }

                const message = payload?.message;
                if (!message) {
                    return;
                }

                list.value.push(message);

                // Read own message when it comes in.
                if (message.origin_meta.type === 'Message Hub' && message.origin_meta.id === Spark.state.user.id) {
                    markAsRead();
                } else if (undefined !== activeThread.value?.unread) {
                    // Mark rest of messages as unread.
                    activeThread.value.unread++;
                }
            })
            .listen('.ThreadUpdated', async ({ thread }) => {
                if (thread.contact_id !== contact.value.id) {
                    return;
                }

                if (!activeThread.value) {
                    await mount(contact.value, currentGroupId.value);
                } else {
                    activeThread.value = {
                        ...activeThread.value,
                        ...thread,
                    };
                }

                // Mark as read if assignee is current user.
                // For case when someone assigneed thread and then user tries to answer and assign thread to him self.
                if (activeThread.value?.assignee_id === Spark.state.user.id) {
                    markAsRead();
                }
            })
            .error?.((e) => {
                if (echoErrors.length <= 3 && !isAuthErrorEcho(e)) {
                    window?.Bugsnag?.notify(e instanceof Error ? e : new Error(JSON.stringify(e)));
                    echoErrors.push(e);
                }
            });
    };

    return {
        mount,
        unmount,
        mounting,
        subscribeEvents,
        unsubscribeEvents,
        markAsRead,
        claimThread,
        changeActiveThreadState,
        loadMessages,
        lastUnreadMessage,
        messageGroups,
        shouldShowNewLine,
        fetchAgents,
        agentName,
        agent,
        agents,
        isOwnThread,
        activeThread,
        list,
        fetchParams,
        loadingMessages,
        fetchingAgents,
        changingThreadState,
        claimingThread,
        markingThreadAsRead,
        actionInProgress,
        isLast,
        isPhoneNumberSet,
        loadingPhoneNumbers,
        isContactSubscribed,
        message,
        resetMessageData,
        isMessagingDisabled,
        getSendingPhoneNumber,
        validateMessage,
        deliverMessage,
        sendingMessage,
    };
});
